/**
 * Utilidades para lectura de datos de autenticación desde localStorage/sessionStorage.
 *
 * Los tokens JWT (access y refresh) ahora se almacenan en cookies httpOnly
 * y NO son accesibles desde JavaScript. Solo los metadatos del usuario
 * se guardan en storage para uso en la UI.
 */

export const AUTH_STATE_CHANGED_EVENT = "auth-state-changed";

function emitAuthStateChanged() {
  window.dispatchEvent(new Event(AUTH_STATE_CHANGED_EVENT));
}

/**
 * Determina si el usuario está autenticado revisando la presencia de userId en storage.
 */
export function isAuthenticated() {
  return !!(localStorage.getItem("userId") || sessionStorage.getItem("userId"));
}

/**
 * Obtiene un valor de localStorage o sessionStorage (busca en ambos).
 */
function getFromStorage(key) {
  return localStorage.getItem(key) || sessionStorage.getItem(key);
}

/**
 * Retorna el storage activo según mantenerSesion.
 */
function getActiveStorage() {
  return localStorage.getItem("mantenerSesion") === "true"
    ? localStorage
    : sessionStorage;
}

export function getUserId() {
  const id = getFromStorage("userId");
  return id ? parseInt(id, 10) : null;
}

export function getUserRole() {
  const role = getFromStorage("userRole");
  return role ? parseInt(role, 10) : null;
}

// Inventario respeta la simulación existente sin cambiar el rol real de la sesión.
export function getInventoryRole() {
  const role = getUserRole();
  const simulated = sessionStorage.getItem('RolDevelop');
  return role === 5 && /^[1-8]$/.test(simulated || '') ? Number(simulated) : role;
}

export function getUserName() {
  return getFromStorage("userName");
}

export function getUserEmail() {
  return getFromStorage("userEmail");
}

export function getUserPlanta() {
  const planta = getFromStorage("userPlanta");
  return planta ? parseInt(planta, 10) : null;
}

export function getUserValidated() {
  return getFromStorage("userValidated") === "true";
}

export function getUserFirma() {
  return getFromStorage("userFirma");
}

export function getImageProfile() {
  return getFromStorage("imageProfile");
}

export function isGoogleAuth() {
  return getFromStorage("googleAauth") === "true";
}

export function requiresPasswordChange() {
  return getFromStorage("mustChangePassword") === "true";
}

export function clearPasswordChangeRequirement() {
  localStorage.setItem("mustChangePassword", "false");
  sessionStorage.setItem("mustChangePassword", "false");
}

/**
 * Guarda los datos del usuario tras un login exitoso.
 * @param {Object} userData - Datos del usuario devueltos por la API.
 * @param {boolean} mantenerSesion - Si true, usa localStorage; si false, sessionStorage.
 * @param {Object} extras - Datos adicionales opcionales (imageProfile, googleAauth, etc.)
 */
export function saveUserData(userData, mantenerSesion, extras = {}) {
  const storage = mantenerSesion ? localStorage : sessionStorage;

  storage.setItem("userId", userData.idusuario);
  storage.setItem("userName", userData.nombreusuario);
  storage.setItem("userRole", userData.idrol);
  storage.setItem(
    "userEmail",
    userData.correoelectronico || userData.email || "",
  );
  storage.setItem("userPlanta", userData.idplanta || "");
  storage.setItem("userValidated", userData.validated || "false");
  storage.setItem("userFirma", userData.firma || "");
  storage.setItem("mantenerSesion", mantenerSesion ? "true" : "false");
  storage.setItem(
    "mustChangePassword",
    userData.mustchangepassword ? "true" : "false",
  );

  if (extras.imageProfile !== undefined) {
    storage.setItem("imageProfile", extras.imageProfile || "");
  } else if (userData.imageProfile) {
    storage.setItem("imageProfile", userData.imageProfile);
  }

  if (extras.googleAauth !== undefined) {
    storage.setItem("googleAauth", extras.googleAauth);
  }

  emitAuthStateChanged();
}

/**
 * Limpia todos los datos de autenticación de ambos storages.
 */
export function clearAuthData() {
  const keys = [
    "userId",
    "userName",
    "userRole",
    "userEmail",
    "userPlanta",
    "userValidated",
    "userFirma",
    "mantenerSesion",
    "imageProfile",
    "googleAauth",
    "mustChangePassword",
    "token",
    "refreshToken",
  ];
  keys.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  // Limpiar también datos de modo desarrollador
  sessionStorage.removeItem("RolDevelop");
  sessionStorage.removeItem("PlantaDevelop");
  emitAuthStateChanged();
}
